import React, {
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import clx from 'classnames'

export type DropdownOption<TValue = string> = {
  label: string
  value: TValue
  keywords?: string[]
  disabled?: boolean
}

type RenderOptionParams<TValue> = {
  option: DropdownOption<TValue>
  isSelected: boolean
  isHighlighted: boolean
}

type RenderSelectedParams<TValue> = {
  selected?: DropdownOption<TValue>
  isOpen: boolean
  placeholder: string
}

type DropdownProps<TValue = string> = {
  portalPosition: 'to-right'|'to-left'
  options: DropdownOption<TValue>[]
  value?: TValue
  onChange: (value: TValue, option: DropdownOption<TValue>) => void
  placeholder?: string
  searchable?: boolean
  disabled?: boolean
  className?: string
  menuClassName?: string
  optionClassName?: string
  searchPlaceholder?: string
  emptyState?: React.ReactNode
  children?:
    | React.ReactNode
    | ((selected?: DropdownOption<TValue>) => React.ReactNode)
  renderOption?: (params: RenderOptionParams<TValue>) => React.ReactNode
  renderSelected?: (params: RenderSelectedParams<TValue>) => React.ReactNode
}

export function Dropdown<TValue = string>({
  portalPosition = 'to-left',
  options,
  value,
  onChange,
  placeholder = 'Select option',
  searchable = true,
  disabled = false,
  className = '',
  menuClassName = '',
  optionClassName = '',
  searchPlaceholder = 'Search...',
  emptyState = 'No results',
  children,
  renderOption,
  renderSelected,
}: DropdownProps<TValue>) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  )

  const filteredOptions = useMemo(() => {
    const normalized = search.trim().toLowerCase()

    if (!normalized) {
      return options
    }

    return options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(normalized)
      const keywordsMatch = option.keywords?.some((keyword) =>
        keyword.toLowerCase().includes(normalized)
      )

      return labelMatch || !!keywordsMatch
    })
  }, [options, search])

  useEffect(() => {
    if (!isOpen) return

    const firstEnabledIndex = filteredOptions.findIndex((option) => !option.disabled)
    setHighlightedIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0)
  }, [isOpen, filteredOptions])

  useEffect(() => {
    if (!isOpen || !searchable) return
    searchInputRef.current?.focus()
  }, [isOpen, searchable])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  const openDropdown = () => {
    if (disabled) return
    setIsOpen(true)
  }

  const closeDropdown = () => {
    setIsOpen(false)
    setSearch('')
  }

  const selectOption = (option: DropdownOption<TValue>) => {
    if (option.disabled) return
    onChange(option.value, option)
    closeDropdown()
  }

  const moveHighlight = (direction: 1 | -1) => {
    if (!filteredOptions.length) return

    let nextIndex = highlightedIndex

    for (let i = 0; i < filteredOptions.length; i += 1) {
      nextIndex += direction

      if (nextIndex < 0) {
        nextIndex = filteredOptions.length - 1
      }

      if (nextIndex >= filteredOptions.length) {
        nextIndex = 0
      }

      if (!filteredOptions[nextIndex]?.disabled) {
        setHighlightedIndex(nextIndex)
        return
      }
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return

    if (!isOpen && (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === ' ')) {
      event.preventDefault()
      openDropdown()
      return
    }

    if (!isOpen) return

    if (event.key === 'Escape') {
      event.preventDefault()
      closeDropdown()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveHighlight(1)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveHighlight(-1)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const option = filteredOptions[highlightedIndex]
      if (option && !option.disabled) {
        selectOption(option)
      }
    }
  }

  const defaultTrigger = (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-2">
      <span className="tw-truncate tw-text-sm tw-text-content-base">
        {selectedOption?.label || placeholder}
      </span>
      <span className="tw-text-xs tw-text-content-base">{isOpen ? '▲' : '▼'}</span>
    </div>
  )

  const triggerContent =
    typeof children === 'function'
      ? children(selectedOption)
      : children ??
        (renderSelected
          ? renderSelected({
              selected: selectedOption,
              isOpen,
              placeholder,
            })
          : defaultTrigger)

  return (
    <div
      ref={rootRef}
      className={`tw-relative tw-inline-block tw-w-[35px] ${className}`}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        className="tw-w-[30px] tw-text-right"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {triggerContent}
      </button>

      {isOpen && (
        <div
          className={clx(`tw-absolute`, {
            'tw-right-0': portalPosition === 'to-right',
          }, `tw-z-[500] tw-mt-1 tw-w-[180px] tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg ${menuClassName}`)}
        >
          {searchable && (
            <div className="tw-border-b tw-border-gray-100 tw-p-2">
              <input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="tw-w-full tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm tw-outline-none focus:tw-border-gray-400"
              />
            </div>
          )}

          <div className="tw-max-h-60 tw-overflow-y-auto" role="listbox">
            {filteredOptions.length === 0 ? (
              <div className="tw-p-3 tw-text-sm tw-text-content-base">{emptyState}</div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value
                const isHighlighted = index === highlightedIndex

                return (
                  <button
                    key={`${String(option.value)}-${index}`}
                    type="button"
                    disabled={option.disabled}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => selectOption(option)}
                    className={`
                      tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-text-left tw-text-sm
                      ${isHighlighted ? 'tw-bg-gray-100' : ''}
                      ${isSelected ? 'tw-font-medium' : ''}
                      ${option.disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'hover:tw-bg-gray-50'}
                      ${optionClassName}
                    `}
                  >
                    {renderOption ? (
                      renderOption({
                        option,
                        isSelected,
                        isHighlighted,
                      })
                    ) : (
                      <>
                        <span className="tw-truncate">{option.label}</span>
                        {isSelected && <span className="tw-text-xs">✓</span>}
                      </>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
